import {useState} from 'react';
import PropTypes from 'prop-types';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import {Row, Col} from 'react-bootstrap';
import withAlerts from '../../lib/withAlerts';
import {compose} from '../../lib/compose';
import Modal from 'react-bootstrap/Modal';
import HDMConfigFormPresets from './hdmConfigFormPresets';
import {useForm} from 'react-hook-form';
import {useHDMConfigMutations} from './hdmConfigMutations';
import { gql } from "@apollo/client";
import { useLazyQuery } from "@apollo/client/react";
import {modes} from './hdmPresets';
import {useCustomerContext} from '../../lib/context/customerContext';

export const HDMConfigModal = (props) => {
  const {register, reset, handleSubmit, setFocus, setValue, setError, formState: { errors } } = useForm({mode: 'onBlur'});
  const [show, setShow] = useState(false);
  const [noPreset, setNoPreset] = useState(false);
  const {success, error} = props.alerts;
  const {create} = useHDMConfigMutations();
  const [getProfiles] = useLazyQuery(PROFILES);
  const {hasPermission} = useCustomerContext();
  const handleShow = () => {
    reset();
    setShow(true);
  }
  const handleClose = () => setShow(false);
  
  const handleSaveConfiguration = async (formData) => {
    try {
      // BUGFIX: the preset checkboxes in HDMConfigFormPresets are rendered
      // with a native `disabled` attribute for display purposes only -
      // react-hook-form excludes disabled inputs from submitted form data
      // (same as native HTML forms), so their is_cellular_off/is_wifi_off/
      // is_bluetooth_off values never actually reached formData.options.
      // Fix: derive those flags directly from the selected predefined_preset
      // via the `modes` lookup (the same source that drives the checkbox
      // display), instead of relying on the checkboxes' own form state.
      const formattedOptions = formData?.options?.reduce((acc, option, index) => {
        if (!option) return acc; // guard against sparse array holes from options.1/options.2 naming

        const mode = modes.find((m) => m.predefined_preset == option.predefined_preset);
        if (!mode) return acc; // guard against an unmatched/bad preset value

        const flags = mode.options.reduce((flagAcc, opt) => ({...flagAcc, ...opt}), {});

        acc.push({
          ...flags,
          predefined_preset: option.predefined_preset,
          sequence: index,
          option_name: mode.pretty,
        });
        return acc;
      }, [])
        ?.filter((option) => (option.predefined_preset > 0 && option.predefined_preset < 8)) ?? [];

      const profile = {
        name: formData?.name,
        options: formattedOptions.flat()
      };

      if(profile.options.length === 0) {
        setNoPreset(true);
        error({msg: 'At least 1 preset option is required.'});
        setFocus('options.1.predefined_preset');
        return;
      }

      const variables = {
        variables: {
          filters: {
            field: "name", value: `${profile?.name}`
          }
        }
      };

      //lazyQuery
      const checkExistingProfile = await getProfiles(variables);
      
      if(checkExistingProfile?.data?.HDMConfigProfiles?.result?.length > 0) {
        setError("name", {
          type: "manual",
          message: "HDM Profile name already exists.",
        })
        error({msg: 'An HDM Profile with that name already exists.'});
        setFocus('name');
        return;
      }
         
      const createProfile = await create({variables: {profile: profile}});
      if(createProfile?.errors) {

        const userFacingError = ('HDM Profile Creation failed.');
        console.error(userFacingError + JSON.stringify(createProfile?.errors));
        error({msg: userFacingError});
        
      } else {
        success({msg: `HDM Profile Added.`});
        handleClose();
      }
    } catch (err) {
      // BUGFIX: this handler previously had no error boundary at all - any
      // thrown exception (e.g. a bad predefined_preset value) silently
      // rejected the async function with no alert and no modal close.
      console.error('HDM Profile creation error: ' + err);
      error({msg: 'HDM Profile Creation failed. Please try again.'});
    }
  }

  const clearErrors = () => setNoPreset(false);

  return (
    <>
      <Button data-testid="create-hdm-profile-button" disabled={!hasPermission('HDM_PROFILE_CREATE')} className="ms-3" onClick={handleShow}>Create HDM Profile</Button>
      <Modal
        {...props}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        show={show}
        onHide={handleClose}
        animation={false}
        className="modal-white"
      >
        <Form className="mb-3">
          <Modal.Header closeButton>
            <Modal.Title>Create HDM Profile</Modal.Title>
          </Modal.Header>
          <Modal.Body>
          <Row>
            <Col>
              <HDMConfigFormPresets 
                register={register}
                setFocus={setFocus}
                errors={errors}
                setValue={setValue}
                noPreset={noPreset}
                clearErrors={clearErrors}
              />
            </Col>
          </Row>
          </Modal.Body>
          <Modal.Footer>
            <div className="d-flex justify-content-end">
              <Button onClick={handleClose} variant="secondary">Cancel</Button> 
              <Button onClick={handleSubmit(handleSaveConfiguration)} className="ms-2">Submit</Button> 
            </div>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}

const PROFILES = gql`
query getHDMConfigProfiles($filters: GenericFilters!) {
  HDMConfigProfiles (filters: $filters) {
    result {
      name
    }
  }
}
`;

HDMConfigModal.propTypes = {
  alerts: PropTypes.shape({
    error: PropTypes.func,
    success: PropTypes.func
  })
}

export default compose(
  withAlerts,
)(HDMConfigModal);
